import type { ErrorResponse } from "@zx/contracts";

import { readPurchaseAs, readViewAs } from "../lib/identity.ts";
import { noteExpired } from "../lib/session.ts";

const BASE = import.meta.env.VITE_API_BASE ?? "/api";

export class ApiError extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly traceId: string,
    readonly status: number,
    /**
     * 🔴 **必须把 `details` 带上。**
     *    后端在这里放的是"页面下一步该怎么办"所需的数据：
     *    - `RATE_LIMITED` 带 `retryAfterSec` —— 没有它倒计时**动不了**，
     *      页面上写着"请 30 秒后再试"却一直停在 30，用户以为界面卡死了，
     *      然后不停点（每点一次还会把等待时间拉长）。
     *    - `FORBIDDEN` 带 `boundShopId` —— 逛到别人的店时要**把他送回自己的店**，
     *      而不是甩一个红色报错就完。
     *
     *    早先这里直接把它丢掉了，上面两个功能**在前端一直是死的**：
     *    服务端老老实实回了，页面拿不到。
     */
    readonly details?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/**
 * 唯一的取数入口。所有请求带 cookie（token 在 httpOnly cookie 里），
 * 失败一律抛 ApiError，页面只认 code，不去猜 HTTP 状态。
 */
async function rawRequest<T>(path: string, init?: RequestInit): Promise<T> {
  /**
   * 🔴 **切到商家身份后，整个商城按他的进货价显示。**
   *    有上级的商家逛首页看到平台指导零售价是没有意义的 ——
   *    那是给陌生人看的最贵一档，他永远不会按那个价买。
   *
   * ⚠️ 加在**唯一的取数入口**上，而不是逐个页面传 ——
   *    漏一个页面就会出现"列表一个价、详情另一个价"，
   *    下单时还会被 `PRICE_CHANGED` 拦下，用户只会觉得系统坏了。
   *
   * ⚠️ 调用方显式传了这个头就以它为准（进货那条路自己指定身份）。
   */
  const purchaseAs = readPurchaseAs();
  /**
   * ⚠️ 两个头**互斥**：一个是"我买的价"、一个是"我卖的价"。
   *    同时发出去的话服务端会按其中一个算，另一个静默失效 ——
   *    页面上显示的是哪个价就全看服务端的判断顺序，谁也说不清。
   */
  const viewAs = purchaseAs ? readViewAs() : "purchase";
  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, {
      credentials: "include",
      ...init,
      headers: {
        /**
         * 🔴 **没有 body 就绝不能声明 `Content-Type: application/json`。**
         *
         *    声明了却发空 body，Fastify 的 JSON 解析器会直接把请求打回来 ——
         *    前端只看到一句"服务器开小差了"。**退出登录就是这么坏的**：
         *    `api.post("/auth/logout")` 不带 body，请求发出去了、也返回了，
         *    但根本没进到处理函数，cookie 一个都没清 —— 点了没反应，
         *    而且看起来像是按钮坏了，其实是请求头的问题。
         *
         *    ⚠️ 这条以前只在 DELETE 上修过一次（取数封装里判过 body），
         *    但同一个毛病对**任何没有 body 的请求**都成立。现在统一在这儿判。
         */
        ...(init?.body ? { "Content-Type": "application/json" } : {}),
        ...(purchaseAs && viewAs === "purchase"
          ? { "X-Purchase-As": String(purchaseAs) }
          : {}),
        ...(purchaseAs && viewAs === "shop" ? { "X-Shop-As": String(purchaseAs) } : {}),
        ...init?.headers,
      },
    });
  } catch {
    /**
     * 🔴 **`fetch` 抛错 = 请求根本没发出去**（断网、服务没起来、DNS 解析不了）。
     *    不接住的话页面会把浏览器的原生错误原样显示出来 ——
     *    一个中文界面上突然冒出一句英文 `Failed to fetch`，
     *    用户完全不知道发生了什么，也不知道该干什么。
     *    包成 ApiError，页面的错误渲染就跟别处一致了。
     */
    throw new ApiError("NETWORK", "连不上服务器，请检查网络后重试", "", 0);
  }

  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as ErrorResponse | null;
    throw new ApiError(
      body?.code ?? (res.status >= 500 ? "SERVER_DOWN" : "INTERNAL"),
      /**
       * 🔴 **服务端没回 JSON 时别说"网络异常"。**
       *    这一档最常见的真实原因是**后端挂了或正在重启**（网关吐一个
       *    502/504 的 HTML 错误页），跟用户的网络一点关系都没有 ——
       *    让他去查自己的 WiFi 是把责任推给他，而且他查了也没用，
       *    只会反复重试同一个动作。
       *
       *    在登录页尤其要命：他会以为是自己账号密码的问题，
       *    一遍遍换密码试 —— 而每试一次，服务端的登录递进等待还会
       *    把冷却时间拉得更长。
       *
       * ⚠️ 状态码要带出来：用户报障时说得出"502"，排查会快很多。
       */
      body?.message ??
        (res.status >= 500
          ? `服务器暂时没有响应，请稍后再试（${res.status}）`
          : `请求没有成功（${res.status}），请稍后再试`),
      body?.traceId ?? "",
      res.status,
      body?.details,
    );
  }

  return res.status === 204 ? (undefined as T) : ((await res.json()) as T);
}

/**
 * ═══════════════════════════════════════════════════════════════
 *  access token 过期了自动续一次
 * ═══════════════════════════════════════════════════════════════
 *
 * 🔴 **这是"页面老提示登录过期"的真正原因。**
 *    access cookie 只有 **15 分钟**，refresh cookie 有 **30 天**，
 *    `/auth/refresh` 这个接口一直是好的 —— **但前端从来没调过它**。
 *    于是登录满 15 分钟，每个请求都 401，整站当成没登录：
 *    正在填的表单没了、后台被守卫弹回登录页。
 *    而那张能换新令牌的 refresh cookie 就在浏览器里躺着，一直没人用。
 *
 * 🔴 **必须单飞（single-flight）。** 一个页面同时发七八个请求，
 *    过期时它们会**一起** 401 —— 各自去续期的话就是七八次 `/auth/refresh`，
 *    而这个接口每次都会**轮换** refresh token：第一个换走之后，
 *    后面几个拿着旧的去换，全部失败，**结果是本来能救回来的会话被自己踢掉**。
 *    所以同一时刻只允许有一个续期在飞，其余的等它。
 *
 * ⚠️ **只重试一次。** 续期完再 401 就是真的没登录了（会话被踢、账号被禁用），
 *    再试下去就是死循环。
 *
 * ⚠️ **认证接口自己不参与续期**：`/auth/refresh` 401 时再去调 `/auth/refresh`
 *    是无限递归；登录/注册的 401 是"密码错了"，不是"过期了"。
 *
 * ⚠️ **续期失败要记住。** 游客逛商城时 `/auth/me` 本来就会 401 ——
 *    不记的话，每次 401 都白打一发 `/auth/refresh`。
 *    记住之后整个会话只多打一次；登录成功时清掉这个标记。
 */
const NO_REFRESH = ["/auth/refresh", "/auth/login", "/auth/register", "/auth/logout"];

let refreshing: Promise<boolean> | null = null;
/** 上一次续期失败过 = 这个浏览器现在就是没登录，别再白打了。 */
let refreshDead = false;

/** 登录/注册成功后调 —— 新会话要允许重新续期。 */
export function resetAuthRefresh(): void {
  refreshDead = false;
}

async function tryRefresh(): Promise<boolean> {
  if (refreshDead) return false;
  /** 🔴 单飞：已经有人在续了就等他的结果，别再发一次（见上面的说明）。 */
  refreshing ??= (async () => {
    try {
      const res = await fetch(`${BASE}/auth/refresh`, {
        method: "POST",
        credentials: "include",
      });
      if (!res.ok) {
        refreshDead = true;
        /**
         * 🔴 **确认是真的掉线了 —— 通知顶部提示条，不要自作主张跳登录页。**
         *    他正用着，页面上可能有填了一半的东西；直接跳走等于把那些吃掉，
         *    而他还没弄明白发生了什么。让他自己选「停留此页 / 重新登录」。
         * ⚠️ `noteExpired` 里判过"登录过一次没有" —— 游客的 401 不会弹。
         */
        noteExpired();
        return false;
      }
      return true;
    } catch {
      /** ⚠️ 网络错误不算"没登录"，别把 `refreshDead` 立起来 —— 网一好就该能续上。 */
      return false;
    } finally {
      refreshing = null;
    }
  })();
  return refreshing;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  try {
    return await rawRequest<T>(path, init);
  } catch (err) {
    const status = (err as ApiError)?.status;
    if (status !== 401 || NO_REFRESH.some((p) => path.startsWith(p))) throw err;
    if (!(await tryRefresh())) throw err;
    /** ⚠️ 只重试这一次 —— 再 401 就是真的没登录了。 */
    return rawRequest<T>(path, init);
  }
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: "POST", body: body === undefined ? undefined : JSON.stringify(body) }),
  put: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: "PUT", body: body === undefined ? undefined : JSON.stringify(body) }),
  patch: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: "PATCH", body: body === undefined ? undefined : JSON.stringify(body) }),
  /**
   * ⚠️ DELETE 也可以带 body（后台删东西要**写理由**，那是本项目的既定要求）。
   *    不传 body 时**绝不能声明 `Content-Type`** —— 声明了却发空 body，
   *    Fastify 的 JSON parser 会直接把请求打回来（500），根本进不到处理函数。
   *    这条判断在 `request` 里统一做了，这里只管传不传。
   */
  del: <T>(path: string, body?: unknown) =>
    request<T>(path, {
      method: "DELETE",
      body: body === undefined ? undefined : JSON.stringify(body),
    }),
};

export function qs(params: Record<string, string | number | undefined>): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== "") sp.set(k, String(v));
  }
  const s = sp.toString();
  return s ? `?${s}` : "";
}
